import { useEffect, useMemo, useState } from "react";
import client from "../api/client";
import { getNextVoucherNo } from "../services/voucherNumberService";
import { useAuth } from "../context/AuthContext";
import { useFinancialYear } from "../context/FinancialYearContext";

const emptyLine = { accountId: "", debit: "", credit: "" };

const styles = {
  page: { fontSize: 11, minHeight: "100%", overflow: "visible", padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "8px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: {
    height: 28,
    fontSize: 11,
    borderRadius: 7,
    fontWeight: 600,
    padding: "3px 9px",
  },
  tableWrap: {
    maxHeight: 400,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 800, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    color: "#fff",
    background: "#07111f",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "5px 6px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  badge: { fontSize: 10, padding: "4px 7px", borderRadius: 20 },
};

export default function JournalPage() {
  const { user } = useAuth();
  const { financialYear } = useFinancialYear();

  const companyId = Number(
    user?.companyId || localStorage.getItem("companyId") || 0,
  );

  const fyFrom = financialYear?.fromDate;
  const fyTo = financialYear?.toDate;
  const fyLocked = Boolean(financialYear?.isLocked || financialYear?.IsLocked);

  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dateLocked, setDateLocked] = useState(false);
  const [checkingDateLock, setCheckingDateLock] = useState(false);

  const [form, setForm] = useState({
    voucherNo: "",
    entryDate: fyFrom || new Date().toISOString().split("T")[0],
    description: "",
    details: [{ ...emptyLine }, { ...emptyLine }],
  });

  useEffect(() => {
    if (fyFrom) {
      setForm((p) => ({
        ...p,
        entryDate: fyFrom,
        voucherNo: "",
      }));
    }
  }, [fyFrom]);

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    loadNextJournalNo();
    checkEntryDateLock(form.entryDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.entryDate, companyId]);

  const selectedDateOutsideFy =
    form.entryDate && fyFrom && fyTo
      ? form.entryDate < fyFrom || form.entryDate > fyTo
      : false;

  const isPostingBlocked =
    fyLocked || dateLocked || selectedDateOutsideFy || checkingDateLock;

  const loadNextJournalNo = async () => {
    try {
      const no = await getNextVoucherNo("journal", form.entryDate);
      setForm((p) => (p.voucherNo ? p : { ...p, voucherNo: no }));
    } catch {
      // ignore auto-number failure
    }
  };

  const checkEntryDateLock = async (entryDate) => {
    if (!entryDate || !companyId) {
      setDateLocked(false);
      return;
    }

    try {
      setCheckingDateLock(true);

      const res = await client.get("/finance/is-date-locked", {
        params: {
          entryDate,
          companyId,
        },
      });

      setDateLocked(Boolean(res.data?.isLocked));
    } catch (err) {
      console.error("Date lock check failed", err);
      setDateLocked(false);
    } finally {
      setCheckingDateLock(false);
    }
  };

  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const res = await client.get("/accounts");
      setAccounts(Array.isArray(res.data) ? res.data : []);
    } catch {
      alert("Unable to load accounts");
    } finally {
      setLoadingAccounts(false);
    }
  };

  const getAccount = (accountId) =>
    accounts.find((a) => String(a.Id ?? a.id) === String(accountId));

  const getAccountText = (account) =>
    `${account?.AccountGroup ?? account?.accountGroup ?? ""} ${
      account?.AccountType ?? account?.accountType ?? ""
    } ${account?.GroupName ?? account?.groupName ?? ""} ${
      account?.Category ?? account?.category ?? ""
    } ${account?.NormalBalance ?? account?.normalBalance ?? ""}`.toLowerCase();

  const getAllowedSide = (accountId) => {
    const account = getAccount(accountId);

    if (!account)
      return {
        debit: true,
        credit: true,
        hint: "Select account first",
      };

    const text = getAccountText(account);

    if (
      text.includes("asset") ||
      text.includes("expense") ||
      text.includes("dr")
    ) {
      return {
        debit: true,
        credit: false,
        hint: "Debit allowed for Asset / Expense accounts",
      };
    }

    if (
      text.includes("liability") ||
      text.includes("equity") ||
      text.includes("capital") ||
      text.includes("income") ||
      text.includes("revenue") ||
      text.includes("cr")
    ) {
      return {
        debit: false,
        credit: true,
        hint: "Credit allowed for Liability / Equity / Income accounts",
      };
    }

    return {
      debit: true,
      credit: true,
      hint: "Account group not detected",
    };
  };

  const addRow = () => {
    if (isPostingBlocked) return;

    setForm((prev) => ({
      ...prev,
      details: [...prev.details, { ...emptyLine }],
    }));
  };

  const removeRow = (index) => {
    if (isPostingBlocked) return;

    if (form.details.length <= 2) return alert("Minimum 2 rows required");

    setForm((prev) => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index),
    }));
  };

  const updateLine = (index, field, value) => {
    const updated = [...form.details];

    if (field === "accountId") {
      updated[index] = {
        ...updated[index],
        accountId: value,
        debit: "",
        credit: "",
      };
      setForm({ ...form, details: updated });
      return;
    }

    const allowed = getAllowedSide(updated[index].accountId);

    if (field === "debit" && !allowed.debit) {
      updated[index].debit = "";
      setForm({ ...form, details: updated });
      return alert(
        "Debit is disabled for this account. This account should be posted on Credit side.",
      );
    }

    if (field === "credit" && !allowed.credit) {
      updated[index].credit = "";
      setForm({ ...form, details: updated });
      return alert(
        "Credit is disabled for this account. This account should be posted on Debit side.",
      );
    }

    updated[index][field] = value;

    if (field === "debit" && Number(value) > 0) updated[index].credit = "";
    if (field === "credit" && Number(value) > 0) updated[index].debit = "";

    setForm({ ...form, details: updated });
  };

  const totalDebit = useMemo(
    () => form.details.reduce((s, r) => s + (parseFloat(r.debit) || 0), 0),
    [form.details],
  );

  const totalCredit = useMemo(
    () => form.details.reduce((s, r) => s + (parseFloat(r.credit) || 0), 0),
    [form.details],
  );

  const difference = Math.abs(totalDebit - totalCredit);
  const isBalanced = difference < 0.01 && totalDebit > 0;

  const canSave = isBalanced && !saving && !isPostingBlocked;

  const saveJournal = async () => {
    if (fyLocked)
      return alert(
        "Selected financial year is locked. Journal cannot be posted.",
      );

    if (dateLocked)
      return alert("Selected entry date belongs to a locked financial year.");

    if (selectedDateOutsideFy)
      return alert("Entry date must be within the selected financial year.");

    if (checkingDateLock)
      return alert("Please wait. Checking financial year lock status.");

    if (!companyId) return alert("Company is required. Please login again.");

    if (!isBalanced) return alert("Debit and Credit must match.");

    const cleanDetails = form.details
      .map((r) => ({
        accountId: Number(r.accountId),
        debit: parseFloat(r.debit) || 0,
        credit: parseFloat(r.credit) || 0,
      }))
      .filter((r) => r.accountId > 0 && (r.debit > 0 || r.credit > 0));

    if (cleanDetails.length < 2)
      return alert("Minimum two valid journal lines are required.");

    const payload = {
      companyId,
      voucherNo: form.voucherNo,
      entryDate: form.entryDate,
      description: form.description,
      details: cleanDetails,
    };

    try {
      setSaving(true);

      await client.post("/finance/journal", payload);

      alert("Saved Successfully");

      setForm({
        voucherNo: "",
        entryDate: fyFrom || new Date().toISOString().split("T")[0],
        description: "",
        details: [{ ...emptyLine }, { ...emptyLine }],
      });
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          err?.response?.data ||
          "Error saving journal",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <h6 style={styles.title}>
              <i className="bi bi-journal-text me-2"></i>
              Journal Entry
            </h6>
            <div style={styles.sub}>Manual double-entry voucher posting</div>
          </div>

          <span
            className={`badge ${isPostingBlocked ? "bg-danger" : "bg-light text-dark"}`}
            style={{ fontSize: 10 }}
          >
            {isPostingBlocked
              ? checkingDateLock
                ? "CHECKING FY"
                : "POSTING BLOCKED"
              : "OPEN"}
          </span>
        </div>

        <div style={styles.body}>
          {checkingDateLock && (
            <div className="alert alert-info py-2" style={{ fontSize: 11 }}>
              Checking financial year lock status...
            </div>
          )}

          {fyLocked && (
            <div className="alert alert-danger py-2" style={{ fontSize: 11 }}>
              <i className="bi bi-lock-fill me-1"></i>
              Selected financial year is locked. Journal posting is blocked.
            </div>
          )}

          {dateLocked && (
            <div className="alert alert-danger py-2" style={{ fontSize: 11 }}>
              <i className="bi bi-lock-fill me-1"></i>
              Selected entry date belongs to a locked financial year. Journal
              posting is blocked.
            </div>
          )}

          {selectedDateOutsideFy && (
            <div className="alert alert-warning py-2" style={{ fontSize: 11 }}>
              <i className="bi bi-exclamation-triangle me-1"></i>
              Entry date must be between {fyFrom} and {fyTo}.
            </div>
          )}

          <div className="row g-2 mb-2">
            <div className="col-md-3">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                placeholder="Voucher No"
                value={form.voucherNo}
                disabled={isPostingBlocked}
                onChange={(e) =>
                  setForm({ ...form, voucherNo: e.target.value })
                }
              />
            </div>

            <div className="col-md-3">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                min={fyFrom || undefined}
                max={fyTo || undefined}
                value={form.entryDate}
                onChange={(e) =>
                  setForm({ ...form, entryDate: e.target.value, voucherNo: "" })
                }
              />
            </div>

            <div className="col-md-6">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                disabled={isPostingBlocked}
                placeholder="Example: Salary expense / Cash deposited into bank / GST adjustment entry"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-primary btn-sm"
                style={styles.btn}
                onClick={addRow}
                disabled={isPostingBlocked}
              >
                <i className="bi bi-plus-circle me-1"></i>
                Add Row
              </button>

              <button
                className={`btn btn-sm ${canSave ? "btn-success" : "btn-secondary"}`}
                style={styles.btn}
                disabled={!canSave}
                onClick={saveJournal}
              >
                <i className="bi bi-save me-1"></i>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>

            <div
              style={{
                fontSize: 10,
                padding: "5px 9px",
                borderRadius: 20,
                background: isBalanced ? "#d1e7dd" : "#f8d7da",
                color: isBalanced ? "#0f5132" : "#842029",
                fontWeight: 700,
              }}
            >
              {isBalanced ? "Balanced" : `Difference: ${difference.toFixed(2)}`}
            </div>
          </div>

          {loadingAccounts && (
            <div className="alert alert-info py-2" style={{ fontSize: 11 }}>
              Loading accounts...
            </div>
          )}

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: 45 }}>#</th>
                  <th style={{ ...styles.th, minWidth: 360 }}>Account</th>
                  <th
                    className="text-end"
                    style={{ ...styles.th, minWidth: 130 }}
                  >
                    Debit
                  </th>
                  <th
                    className="text-end"
                    style={{ ...styles.th, minWidth: 130 }}
                  >
                    Credit
                  </th>
                  <th style={{ ...styles.th, width: 80 }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {form.details.map((row, i) => {
                  const allowed = getAllowedSide(row.accountId);

                  return (
                    <tr key={i}>
                      <td style={styles.td}>{i + 1}</td>

                      <td style={styles.td}>
                        <select
                          className="form-select form-select-sm"
                          style={styles.input}
                          value={row.accountId}
                          disabled={isPostingBlocked}
                          onChange={(e) =>
                            updateLine(i, "accountId", e.target.value)
                          }
                        >
                          <option value="">Select Account</option>
                          {accounts.map((a) => (
                            <option key={a.Id ?? a.id} value={a.Id ?? a.id}>
                              {(a.AccountCode ?? a.accountCode) +
                                " - " +
                                (a.AccountName ?? a.accountName)}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td style={styles.td}>
                        <input
                          type="number"
                          className="form-control form-control-sm text-end"
                          style={styles.input}
                          value={row.debit}
                          disabled={isPostingBlocked || !allowed.debit}
                          title={allowed.hint}
                          placeholder={allowed.debit ? "0.00" : "Locked"}
                          onChange={(e) =>
                            updateLine(i, "debit", e.target.value)
                          }
                        />
                      </td>

                      <td style={styles.td}>
                        <input
                          type="number"
                          className="form-control form-control-sm text-end"
                          style={styles.input}
                          value={row.credit}
                          disabled={isPostingBlocked || !allowed.credit}
                          title={allowed.hint}
                          placeholder={allowed.credit ? "0.00" : "Locked"}
                          onChange={(e) =>
                            updateLine(i, "credit", e.target.value)
                          }
                        />
                      </td>

                      <td style={styles.td}>
                        <button
                          className="btn btn-danger btn-sm"
                          style={{
                            ...styles.btn,
                            height: 25,
                            padding: "2px 8px",
                          }}
                          disabled={isPostingBlocked}
                          onClick={() => removeRow(i)}
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr>
                  <td
                    colSpan="2"
                    style={{
                      ...styles.td,
                      fontWeight: 700,
                      background: "#f8f9fa",
                    }}
                  >
                    Total
                  </td>
                  <td
                    className="text-end"
                    style={{ ...styles.td, fontWeight: 700 }}
                  >
                    {totalDebit.toFixed(2)}
                  </td>
                  <td
                    className="text-end"
                    style={{ ...styles.td, fontWeight: 700 }}
                  >
                    {totalCredit.toFixed(2)}
                  </td>
                  <td style={styles.td}>
                    <span
                      className={`badge ${
                        isBalanced ? "text-bg-success" : "text-bg-danger"
                      }`}
                      style={styles.badge}
                    >
                      {isBalanced ? "OK" : "Diff"}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
